/** @graphql agg **/
import { graphql } from 'react-relay';

import { ConfluencePageAri } from '@atlassian/ari';

import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import type {
	preloadIncomingLinksSampleQuery$data,
	preloadIncomingLinksSampleQuery$variables,
} from './__generated__/preloadIncomingLinksSampleQuery.graphql.ts';

const INCOMING_LINKS_SAMPLE_LIMIT = 3;
const SOURCE_COLUMN = 'source';

type IncomingLinksSampleUnavailableReason = 'invalid-input' | 'malformed-response' | 'query-error';
type IncomingLinksSampleCauseCategory = 'error' | 'graphql' | 'none' | 'non-error';

export type IncomingLinksSampleResult =
	| Readonly<{
			status: 'available';
			sourceAris: readonly string[];
	  }>
	| Readonly<{
			status: 'unavailable';
			reason: IncomingLinksSampleUnavailableReason;
	  }>;

const IncomingLinksSampleQuery = graphql`
	query preloadIncomingLinksSampleQuery($cypherQuery: String!, $first: Int!) {
		graphStore_cypherQueryV2(query: $cypherQuery, first: $first)
			@optIn(to: "GraphStoreCypherQueryV2") {
			edges {
				node {
					columns {
						key
						value {
							__typename
							... on GraphStoreCypherQueryV2AriNode {
								id
							}
						}
					}
				}
			}
		}
	}
`;

const unavailable = (reason: IncomingLinksSampleUnavailableReason): IncomingLinksSampleResult => ({
	status: 'unavailable',
	reason,
});

const buildIncomingLinksCypher = (targetAri: string): string =>
	[
		`MATCH (source)-[:content_referenced_entity]->(target:ConfluencePage {ari: "${targetAri}"})`,
		'WHERE source <> target',
		'RETURN source',
		`LIMIT ${INCOMING_LINKS_SAMPLE_LIMIT}`,
	].join('\n');

const normalizeIncomingLinksSample = (
	data: preloadIncomingLinksSampleQuery$data | null,
): IncomingLinksSampleResult => {
	const edges = data?.graphStore_cypherQueryV2?.edges;
	if (!edges || edges.length > INCOMING_LINKS_SAMPLE_LIMIT) {
		return unavailable('malformed-response');
	}

	const sourceAris: string[] = [];
	for (const edge of edges) {
		const columns = edge.node.columns;
		if (columns.length !== 1) {
			return unavailable('malformed-response');
		}

		const column = columns[0];
		if (
			column.key !== SOURCE_COLUMN ||
			column.value?.__typename !== 'GraphStoreCypherQueryV2AriNode' ||
			typeof column.value.id !== 'string' ||
			!column.value.id.startsWith('ari:')
		) {
			return unavailable('malformed-response');
		}

		sourceAris.push(column.value.id);
	}

	return { status: 'available', sourceAris };
};

const reportUnavailable = (
	reason: Exclude<IncomingLinksSampleUnavailableReason, 'invalid-input'>,
	cause?: unknown,
) => {
	const causeCategory: IncomingLinksSampleCauseCategory =
		cause == null
			? 'none'
			: cause instanceof Error
				? cause.name === 'GraphQLError'
					? 'graphql'
					: 'error'
				: 'non-error';

	ssrPrepareErrorsClient.submitError(
		new Error(`Incoming links sample unavailable: ${reason}`, { cause }),
		{
			operation: 'preload-incoming-links-sample',
			reason,
			causeCategory,
		},
	);
};

export const preloadIncomingLinksSample = async ({
	cloudId,
	contentId,
}: {
	cloudId: string;
	contentId: string;
}): Promise<IncomingLinksSampleResult> => {
	if (!cloudId.trim() || !/^[1-9]\d*$/.test(contentId)) {
		return unavailable('invalid-input');
	}

	let targetAri: string;
	try {
		targetAri = ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString();
	} catch {
		return unavailable('invalid-input');
	}

	try {
		const { data, errors, hasErrors } = await queryAgg<
			preloadIncomingLinksSampleQuery$data,
			preloadIncomingLinksSampleQuery$variables
		>({
			query: IncomingLinksSampleQuery,
			variables: {
				cypherQuery: buildIncomingLinksCypher(targetAri),
				first: INCOMING_LINKS_SAMPLE_LIMIT,
			},
			fetchPolicy: 'no-cache',
			context: {
				headers: {
					'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
				},
			},
		});

		if (hasErrors || errors?.length) {
			reportUnavailable('query-error', errors?.[0]);
			return unavailable('query-error');
		}

		const result = normalizeIncomingLinksSample(data);
		if (result.status === 'unavailable') {
			reportUnavailable('malformed-response');
		}
		return result;
	} catch (cause) {
		reportUnavailable('query-error', cause);
		return unavailable('query-error');
	}
};
